import { ChangeEvent } from "react"
import { TextField, MenuItem, Divider, Box } from "@mui/material"

import changeImageSize from "utils/changeImageSize"
import { netToGross } from "@secureo/common/utils/tax/taxUtils"
import { makeStyles } from "makeStyles"

import { ProductConfigurationsBySku } from "@secureo/common/reducers/productDetailPageReducer"
import {
	ConfigurationGroup,
	ConfigurationGroupProductsBySku,
} from "@secureo/common/typings/Product"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const useStyles = makeStyles()(() => ({
	gridItem: {
		flexBasis: "50%",
		position: "relative",
		padding: "10px",
		boxSizing: "border-box",
	},
	gridItemContent: {
		display: "flex",
	},
	"@media only screen and (max-width: 1333px)": {
		gridItem: {
			flexBasis: "50%",
		},
	},
	"@media only screen and (max-width: 815px)": {
		gridItem: {
			flexBasis: "100%",
		},
	},
}))

const ConfigurationGroupDropDown = ({
	isLoading,
	countryCode,
	taxRate,
	configurationGroup,
	configurationGroupProductsBySku,
	selectedConfigurationsBySku,
	setSingleConfigurationGroupConfiguration,
}: Props) => {
	const { classes } = useStyles()
	const { name: configurationGroupName, description, items } = configurationGroup
	const value = Object.values(selectedConfigurationsBySku)?.[0]?.sku ?? items?.[0]?.sku

	const image = configurationGroupProductsBySku[configurationGroup?.items?.[0]?.sku]?.image

	const changeConfiguration = (event: ChangeEvent<HTMLInputElement>) => {
		const sku = event.target.value

		setSingleConfigurationGroupConfiguration(
			configurationGroupProductsBySku,
			configurationGroupName,
			sku,
		)
	}

	return (
		<Box
			mb={2}
			p={1}
			style={{
				background: "#F7FAFA",
				borderRadius: "3px",
				marginTop: "2rem",
			}}
		>
			<Box ml={1}>
				<h2 style={{ margin: 0 }}>{configurationGroupName}</h2>
				<p
					dangerouslySetInnerHTML={{
						__html: description,
					}}
				/>
			</Box>
			<Divider />
			<div className={classes.gridItem}>
				<div className={classes.gridItemContent}>
					<div style={{ width: "100%" }}>
						{image && (
							<div>
								<img
									alt={configurationGroupName}
									style={{
										maxWidth: "60px",
										marginRight: "15px",
										padding: "3px",
										border: "1px solid lightgrey",
									}}
									src={changeImageSize(image, "small")}
								/>
							</div>
						)}
						<TextField
							id={configurationGroupName}
							label={configurationGroupName}
							name={configurationGroupName}
							value={value}
							onChange={changeConfiguration}
							margin="normal"
							variant="outlined"
							style={{ minWidth: "200px", maxWidth: "380px" }}
							select
							fullWidth
							disabled={isLoading}
						>
							{items.map((configurationGroupItem) => {
								const { sku } = configurationGroupItem
								const configurationProduct = configurationGroupProductsBySku?.[sku]

								if (!configurationProduct) return null

								const netPriceCents =
									configurationProduct?.pricesByCountryCode?.[countryCode] ?? 0
								const grossPriceCents = Math.round(
									netToGross(netPriceCents, taxRate),
								)

								return (
									<MenuItem key={sku} value={sku}>
										{configurationProduct?.name} - zzgl. {grossPriceCents} €
									</MenuItem>
								)
							})}
						</TextField>
					</div>
				</div>
			</div>
		</Box>
	)
}

interface Props {
	isLoading: boolean
	countryCode: CountryCode
	taxRate: number
	configurationGroup: ConfigurationGroup
	configurationGroupProductsBySku: ConfigurationGroupProductsBySku
	selectedConfigurationsBySku: ProductConfigurationsBySku
	setSingleConfigurationGroupConfiguration: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
	) => void
}

export default ConfigurationGroupDropDown
